

















































































































































import ContentCard from '@/components/ContentCard.vue';
import useDefaultStatusS2Selections from '@/composables/useDefaultStatusS2Selections';
import {
  Authority,
  DeviceModel,
  useGetAuthoritiesQuery,
  useCheckExistingDeviceQuery,
  useGetDevicesQuery,
  useGetTemplatesQuery
} from '@/graphql/types';
import { S2Selection } from '@/types/s2selection';
import { computed, defineComponent, onMounted, onUpdated, Ref, ref, watch } from '@vue/composition-api';
import Select2 from '@/components/Select2.vue';
import { FilterState, TelemetryFilterState } from '@/types/enums';
import { useResult } from '@vue/apollo-composable';
import useTelemetryDataFilterStateS2Selections from '@/composables/useTelemetryDataFilterStateS2Selections';
import CommonHelper from '@/utilities/common.helper';
import useProfile from '@/composables/useProfile';
import useNavigateToDevice from '@/composables/useNavigateToDevice';
import Table from '@/components/Table.vue';
import { TableField } from '@/types/TableField';

export default defineComponent({
  components: { ContentCard, Select2, Table },
  setup(_, { root }) {
    const defaultSort = ref({
      sortBy: 'deviceId',
      sortDesc: false
    });
    const perPage = 40;
    const currentPage = ref(1);
    const isFormOpening = ref(false);
    const user = useProfile();
    const deviceNameFilter = ref(null as string | null);
    const deviceIdFilter = ref(null as string | null);
    const stationNumberFilter = ref(null as string | null);
    const statusList = useDefaultStatusS2Selections(user.isAdmin.value);
    const statusFilter = ref(null as S2Selection<FilterState> | null);
    statusFilter.value = statusList[0];
    const templateSelections = [new S2Selection<string>('All', 'Alle skabeloner', null, null)];
    const filteredTemplateSelections = ref([new S2Selection<string>('All', 'Alle skabeloner', null, null)]);
    const templateFilter = ref(null as S2Selection<string> | null);
    templateFilter.value = templateSelections[0];
    const telemetryDataSelections = useTelemetryDataFilterStateS2Selections();
    const telemetryDataFilter = ref(null as S2Selection<TelemetryFilterState> | null);
    telemetryDataFilter.value = telemetryDataSelections[0];
    const authorityFilter = ref(null as S2Selection<Authority> | null);
    let authoritiesSelections: S2Selection<Authority>[] = [];
    const authoritiesQuery = useGetAuthoritiesQuery({
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first'
    });
    const isFirstTimeLoggedInRedirect = ref(false);
    const devices = ref<any[]>([]);
    const sortedDeviceItems = ref<any[]>([]);
    const activeDevices = ref([] as DeviceModel[]);
    const filtered = ref([] as DeviceModel[]);
    let checkExistingDeviceQuery: any;

    onMounted(() => window.addEventListener('popstate', () => (isFormOpening.value = false)));

    onUpdated(() => {
      // This is for direct-link with re-authentication case
      if (!isFirstTimeLoggedInRedirect.value) {
        isFirstTimeLoggedInRedirect.value = true;
        const deviceId = root.$route.query.deviceId;
        history.pushState({}, '', root.$route.path);
        setTimeout(() => {
          if (deviceId) {
            const existingDevice = useResult(checkExistingDeviceQuery.result);
            if (existingDevice.value) {
              navigateToDevice(root.$router, null, deviceId.toString());
            }
          }
        }, 1000);
      }
    });

    const enableFetchingDevices = ref(false);
    watch(
      () => useResult(authoritiesQuery.result).value,
      newValue => {
        if (newValue) {
          newValue
            .filter(x => x.isActive)
            .forEach(element => {
              authoritiesSelections?.push(new S2Selection<Authority>(element.id, element.name, element, null));
            });
          authoritiesSelections.sort((a, b) => a.text.localeCompare(b.text, 'da'));
          authorityFilter.value = authoritiesSelections.find(x => x.object?.cvr == user.profile.value.cvr) ?? null;
          if (!authorityFilter.value && user.isAdmin.value) {
            authorityFilter.value = authoritiesSelections[0];
          }
        }
      },
      { immediate: true }
    );

    watch(
      () => authorityFilter,
      newValue => {
        if (newValue) {
          enableFetchingDevices.value = true;
          currentPage.value = 1;

          const deviceId = root.$route.query.deviceId;
          if (deviceId) {
            checkExistingDeviceQuery = useCheckExistingDeviceQuery(
              () => ({
                organisationId: authorityFilter.value?.id,
                deviceId: deviceId.toString()
              }),
              () => ({
                enabled: enableFetchingDevices.value && !!authorityFilter.value,
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-first'
              })
            );
          }
        }
      },
      { immediate: true }
    );

    const devicesQuery = useGetDevicesQuery(
      () => ({
        id: authorityFilter.value?.id
      }),
      () => ({
        enabled: enableFetchingDevices.value && !!authorityFilter.value,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first'
      })
    );

    const templatesQuery = useGetTemplatesQuery({
      enabled: enableFetchingDevices.value,
      fetchPolicy: 'cache-and-network'
    });

    watch(
      () => useResult(devicesQuery.result).value,
      newValue => {
        if (newValue) {
          const deviceItems: any[] = [];
          newValue.forEach(d =>
            deviceItems.push({
              id: d.id,
              organisationId: d.organisationId,
              templateId: d.templateId,
              deviceId: d.deviceId ?? '',
              name: d.name ?? '',
              templateName: d.templateName ?? '',
              isActive: d.isActive,
              stationNumber: d.stationNumber ?? '',
              telemetryData: d.telemetryData,
              latestPayload: latestPayload(d)
            })
          );
          devices.value = [...deviceItems];
          setSortedDevices();
        }
      },
      { immediate: true }
    );

    watch(
      () => devices.value,
      newValue => {
        if (newValue) {
          activeDevices.value = devices.value.filter(x => x.isActive);
          setFilteredTemplateSelections();
        }
      },
      { immediate: true }
    );

    watch(
      () => currentPage.value,
      newValue => {
        setSortedDevices(filtered.value);
      },
      { immediate: true }
    );

    root.$on('refetchDevices', (newDevice: any) => {
      if (newDevice) {
        const template = templateSelections.find(t => t.id === newDevice.templateId);
        if (template) {
          newDevice.templateName = template.text;
        }
        newDevice.latestPayload = latestPayload(newDevice);
        devices.value.push(newDevice);
        filtered.value = [newDevice, ...filtered.value];
        setTimeout(() => {
          setSortedDevices(filtered.value);
        }, 1000);
      } else {
        devicesQuery.refetch();
      }
    });

    watch(
      () => useResult(templatesQuery.result).value,
      newValue => {
        if (newValue) {
          newValue.forEach(element => {
            templateSelections?.push(new S2Selection<string>(element.id, element.name, element.id, null));
          });
          templateSelections.sort((a, b) => a.text.localeCompare(b.text, 'da'));
        }
      },
      { immediate: true }
    );

    function useFilteringDevices(
      devices: Ref<DeviceModel[]>,
      statusFilter: Ref<S2Selection<FilterState> | null>,
      templateFilter: Ref<S2Selection<string> | null>,
      telemetryDataFilter: Ref<S2Selection<TelemetryFilterState> | null>,
      deviceNameFilter: Ref<string | null>,
      deviceIdFilter: Ref<string | null>,
      stationNumberFilter: Ref<string | null>
    ) {
      watch(
        [
          devices,
          statusFilter,
          templateFilter,
          telemetryDataFilter,
          deviceNameFilter,
          deviceIdFilter,
          stationNumberFilter
        ],
        () => {
          // Only do the filter when the child page (Device) is not opening
          if (!isFormOpening.value) {
            currentPage.value = 1;

            filter(
              devices.value,
              statusFilter.value?.object ?? null,
              templateFilter.value?.object ?? null,
              telemetryDataFilter.value?.object ?? null,
              deviceNameFilter.value,
              deviceIdFilter.value,
              stationNumberFilter.value
            );

            setSortedDevices(filtered.value);
          }
        }
      );

      async function filter(
        devices: DeviceModel[],
        statusFilter: FilterState | null,
        templateFilter: string | null,
        telemetryDataFilter: TelemetryFilterState | null,
        deviceNameFilter: string | null,
        deviceIdFilter: string | null,
        stationNumberFilter: string | null
      ) {
        filtered.value = devices.filter(element => {
          var isValid = true;

          if (statusFilter === FilterState.Inactive) {
            isValid = element.isActive === false;
          }

          if (statusFilter === FilterState.Active) {
            isValid = element.isActive === true;
          }

          if (templateFilter && isValid) {
            isValid = element.templateId == templateFilter;
          }

          if (telemetryDataFilter && isValid) {
            switch (telemetryDataFilter) {
              case TelemetryFilterState.NoData: {
                isValid = !element.telemetryData;
                break;
              }
              case TelemetryFilterState.NoDataIn24Hours: {
                isValid =
                  !element.telemetryData ||
                  (!!element.telemetryData &&
                    CommonHelper.calculateDaysDiff(new Date(), new Date(element.telemetryData.dataLastReceived)) > 1);
                break;
              }
              case TelemetryFilterState.NoDataIn3Days: {
                isValid =
                  !element.telemetryData ||
                  (!!element.telemetryData &&
                    CommonHelper.calculateDaysDiff(new Date(), new Date(element.telemetryData.dataLastReceived)) > 3);
                break;
              }
              case TelemetryFilterState.NoDataIn1Week: {
                isValid =
                  !element.telemetryData ||
                  (!!element.telemetryData &&
                    CommonHelper.calculateDaysDiff(new Date(), new Date(element.telemetryData.dataLastReceived)) > 7);
                break;
              }
              case TelemetryFilterState.HaveDataIn24Hours: {
                isValid =
                  !!element.telemetryData &&
                  CommonHelper.calculateDaysDiff(new Date(), new Date(element.telemetryData.dataLastReceived)) <= 1;
                break;
              }
              case TelemetryFilterState.HaveDataIn3Days: {
                isValid =
                  !!element.telemetryData &&
                  CommonHelper.calculateDaysDiff(new Date(), new Date(element.telemetryData.dataLastReceived)) <= 3;
                break;
              }
              case TelemetryFilterState.HaveDataIn1Week: {
                isValid =
                  !!element.telemetryData &&
                  CommonHelper.calculateDaysDiff(new Date(), new Date(element.telemetryData.dataLastReceived)) <= 7;
                break;
              }
              case TelemetryFilterState.HaveDataIn30Days: {
                isValid =
                  !!element.telemetryData &&
                  CommonHelper.calculateDaysDiff(new Date(), new Date(element.telemetryData.dataLastReceived)) <= 30;
                break;
              }
            }
          }

          if (deviceNameFilter && isValid && element && element.name) {
            isValid = element.name.toLowerCase().indexOf(deviceNameFilter.toLowerCase()) >= 0;
          }

          if (deviceIdFilter && isValid && element && element.deviceId) {
            isValid = element.deviceId.toLowerCase().indexOf(deviceIdFilter.toLowerCase()) >= 0;
          }

          if (stationNumberFilter && isValid && element && element.stationNumber) {
            if (!element.stationNumber?.length) isValid = false;
            else isValid = element.stationNumber.toLowerCase().indexOf(stationNumberFilter.toLowerCase()) >= 0;
          }

          return isValid;
        });
      }
      return filtered;
    }

    function setFilteredTemplateSelections() {
      const uniqueTemplateIds = devices.value
        .map(item => item.templateId)
        .filter((value, index, self) => self.indexOf(value) === index);

      const selections = [new S2Selection<string>('All', 'Alle', null, null)];
      const filteredSelections = templateSelections.filter((template: S2Selection<string>) =>
        uniqueTemplateIds.includes(template.id)
      );

      filteredTemplateSelections.value = [...selections, ...filteredSelections];
    }

    function navigateToDevice(router: any, organisationId: string | null, deviceId: string | null) {
      isFormOpening.value = true;
      useNavigateToDevice(router, organisationId, deviceId);
    }

    function latestPayload(device: any) {
      if (device.telemetryData) {
        return device.telemetryData.dataLastReceived;
      }

      return 'Ingen';
    }

    function setSortedDevices(items: any = null) {
      // Only sort devices when the child page (Device) is not opening
      if (!isFormOpening.value) {
        const asc = (a: any, b: any) => a[defaultSort.value.sortBy]?.localeCompare(b[defaultSort.value.sortBy], 'da');
        const desc = (a: any, b: any) =>
          b[defaultSort.value.sortBy]?.localeCompare(a[defaultSort.value.sortBy], 'da');
        filtered.value = filtered.value.sort(defaultSort.value.sortDesc ? desc : asc);
        sortedDeviceItems.value = (items ?? filtered.value).slice(
          (currentPage.value - 1) * perPage,
          (currentPage.value - 1) * perPage + perPage
        );
      }
    }

    function onSortChanged($event: any) {
      defaultSort.value = {
        sortBy: $event.sortBy,
        sortDesc: $event.sortDesc
      };
      setSortedDevices();
    }

    return {
      loading: computed(() => devicesQuery.loading.value),
      error: computed(() => devicesQuery.error.value),
      authoritySelectionDisabled: computed(() => {
        if (!user.isAdmin.value && user.isAuthority.value) {
          return true;
        }
        if (user.isReadOnly.value) {
          return !user.inDmpOrganization.value;
        }
        return false;
      }),
      isReadOnly: user.isReadOnly,
      statusList,
      templateSelections,
      filteredTemplateSelections,
      telemetryDataSelections,
      authoritiesSelections,
      activeDevices,
      authoritiesLoading: computed(() => authoritiesQuery.loading.value),
      sortedDeviceItems: computed(() => sortedDeviceItems.value),
      devices: useFilteringDevices(
        devices,
        statusFilter,
        templateFilter,
        telemetryDataFilter,
        deviceNameFilter,
        deviceIdFilter,
        stationNumberFilter
      ),
      statusFilter,
      templateFilter,
      telemetryDataFilter,
      deviceNameFilter,
      deviceIdFilter,
      stationNumberFilter,
      authorityFilter,
      navigateToDevice,
      perPage,
      currentPage,
      onSortChanged,
      fields: computed<TableField[]>(() => [
        {
          key: 'deviceId',
          sortable: true,
          label: root.$tc('devices.fields.device_id'),
          width: '10%'
        },
        {
          key: 'name',
          sortable: true,
          label: root.$tc('devices.fields.name'),
          width: '30%'
        },
        {
          key: 'stationNumber',
          sortable: true,
          label: root.$tc('devices.fields.station_number'),
          width: '16%'
        },
        {
          key: 'templateName',
          sortable: true,
          label: root.$tc('devices.fields.template'),
          width: '20%'
        },
        {
          key: 'latestPayload',
          formatter: (value, key, item) => latestPayload(item),
          sortable: true,
          sortByFormatted: true,
          label: root.$tc('devices.fields.latest_payload'),
          width: '15%'
        }
      ])
    };
  }
});
